import { useCallback, useMemo } from 'react'

// project import
import { useGetAccountsQuery } from 'store/api/merklebaseAPI'
import useLinks from './useLinks'
import { mapAccounts } from 'utils/mappers/accounts'
import usePortfolios from './usePortfolios'
import { providersByName } from 'merklebase-utils/commons'

// ----------------------------------------------------------------------

export default function useAccounts(
  { ignorePortfolios = undefined, showAllPortfoliosAccounts = true } = {
    ignorePortfolios: undefined,
    showAllPortfoliosAccounts: true,
  }
) {
  const {
    portfoliosById: { data: portfoliosById },
  } = usePortfolios()

  const {
    linksById: { data: linksById /*, isLoading: isLinksLoading*/ },
  } = useLinks()

  const { data: accounts = [], isLoading: isAccountsLoading /*, isFetching: isAccountsFetching*/ } =
    useGetAccountsQuery({
      ignorePortfolios,
      showAllPortfoliosAccounts,
    })

  const subAccountsByAccountId = useMemo(() => {
    return accounts.reduce((acc, account) => {
      acc[account.id] = accounts.filter((a) => a.account_parent_id === account.id)
      return acc
    }, {})
  }, [accounts])

  const portfoliosCountByAccountId = useMemo(() => {
    return accounts.reduce((acc, account) => {
      if (!acc[account.id]) {
        acc[account.id] = 0
      }

      if (account.portfolio_id) {
        acc[account.id] = acc[account.id] + 1
      }

      return acc
    }, {})
  }, [accounts])

  const mappedAccounts = useMemo(
    () =>
      accounts.map((account) =>
        mapAccounts({
          account,
          allAccounts: accounts,
          linksById,
          portfoliosById,
          portfoliosCountByAccountId,
          subAccountsByAccountId,
        })
      ),
    [accounts, linksById, portfoliosById, portfoliosCountByAccountId, subAccountsByAccountId]
  )

  const counterPartyAccounts = useMemo(
    () =>
      Object.values(
        mappedAccounts.reduce((acc, account) => {
          if (account.portfolio?.isCounterparty) {
            acc[account.id] = account
          }
          return acc
        }, {})
      ),
    [mappedAccounts]
  )

  const counterPartyAccountsById = useMemo(
    () =>
      counterPartyAccounts.reduce((acc, account) => {
        acc[account.id] = account
        return acc
      }, {}),
    [counterPartyAccounts]
  )

  const accountsById = useMemo(
    () =>
      mappedAccounts.reduce((acc, account) => {
        acc[account.id] = account
        return acc
      }, {}),
    [mappedAccounts]
  )

  const defiProviderTickers = useMemo(() => {
    return Object.values(providersByName).reduce((acc, provider) => {
      if (provider.type === 'defi') {
        acc.push(provider.ticker)
      }

      return acc
    }, [])
  }, [])

  const defiAccountsById = useMemo(
    () =>
      mappedAccounts.reduce((acc, account) => {
        if (defiProviderTickers.includes(account.provider.ticker)) {
          acc[account.id] = account
        }

        return acc
      }, {}),
    [defiProviderTickers, mappedAccounts]
  )

  const getAccountsByProvider = useCallback(
    (filter = '') => {
      let includedAccountsMap = {}
      const filterStr = filter.toLowerCase()
      const accountsByProvider = mappedAccounts
        .filter((account) => {
          const matchesAccount =
            account.name.toLowerCase().includes(filterStr) ||
            account.parentAccount?.name.toLowerCase().includes(filterStr) ||
            (account.provider?.name && account.provider.name.toLowerCase().includes(filterStr)) ||
            (account.provider?.ticker && account.provider.ticker.toLowerCase().includes(filterStr))

          if (account.parentAccount) return matchesAccount
          else
            return (
              matchesAccount ||
              account.subAccounts.find((subAccount) =>
                subAccount.name.toLowerCase().includes(filterStr)
              )
            )
        })
        .reduce((acc, account) => {
          const provider = account.provider?.ticker
          acc[provider] = acc[provider] || {}

          if (account.parentAccount) {
            let parentAccount = includedAccountsMap[account.parentAccount.id]
            if (!parentAccount) {
              acc[provider][account.parentAccount.id] = {
                subAccounts: {},
                account,
                ...acc[provider][account.parentAccount.id],
              }
              includedAccountsMap[account.parentAccount.id] =
                acc[provider][account.parentAccount.id]
              parentAccount = acc[provider][account.parentAccount.id]
            }
            parentAccount.subAccounts[account.id] = { account, subAccounts: {} }
            includedAccountsMap[account.id] = parentAccount.subAccounts[account.id]
          } else {
            acc[provider][account.id] = {
              subAccounts: {},
              account,
              ...acc[provider][account.id],
            }
            includedAccountsMap[account.id] = acc[provider][account.id]
          }

          return acc
        }, {})

      return accountsByProvider
    },
    [mappedAccounts]
  )

  const accountsByProvider = useMemo(() => {
    const accountsByProvider = {}

    mappedAccounts.forEach((account) => {
      const provider = account.provider?.ticker
      if (!accountsByProvider[provider]) {
        accountsByProvider[provider] = []
      }

      accountsByProvider[provider] = [...accountsByProvider[provider], account]
    })

    return accountsByProvider
  }, [mappedAccounts])

  return {
    accounts: {
      data: mappedAccounts,
      isLoading: isAccountsLoading /*|| isAccountsFetching || isLinksLoading*/,
    },
    counterPartyAccountsById: {
      data: counterPartyAccountsById,
      isLoading: isAccountsLoading /*|| isAccountsFetching || isLinksLoading*/,
    },
    counterPartyAccounts: {
      data: counterPartyAccounts,
      isLoading: isAccountsLoading /*|| isAccountsFetching || isLinksLoading*/,
    },
    accountsById: {
      data: accountsById,
      isLoading: isAccountsLoading /*|| isAccountsFetching || isLinksLoading*/,
    },
    defiAccountsById: {
      data: defiAccountsById,
      isLoading: isAccountsLoading /*|| isAccountsFetching || isLinksLoading*/,
    },
    accountsByProvider: {
      data: accountsByProvider,
      isLoading: isAccountsLoading /*|| isAccountsFetching || isLinksLoading*/,
    },
    getAccountsByProvider,
  }
}
